import {
  ApolloClient,
  ApolloLink,
  FetchPolicy,
  gql,
  InMemoryCache,
  OperationVariables,
  TypedDocumentNode,
  HttpLink,
  CombinedGraphQLErrors,
  ServerError,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { SetContextLink } from '@apollo/client/link/context';
import { DocumentNode } from 'graphql';
import { errorMessages } from '@/utils/errorMessages';
import { getLocalStorageValue, isRouteProtected, localStorageKey, logout, showToast } from '@/utils/utils';

const getInitialGraphqlUrl = () => {
  // Set NEXT_PUBLIC_IS_LOCAL_APP to the explicit string 'true' at build time to enable
  // the local GraphQL URL override for development and testing client bundles.
  if (process.env.NEXT_PUBLIC_IS_LOCAL_APP === 'true' && process.env.NEXT_PUBLIC_GRAPHQL_URL) {
    return process.env.NEXT_PUBLIC_GRAPHQL_URL;
  }

  return '/api';
};

// Runtime GraphQL URL cache, populated by _app.tsx on mount
let _graphqlUrl = getInitialGraphqlUrl();

export function setGraphqlUrl(url: string) {
  _graphqlUrl = url;
}

// Create an HTTP Link — lazy URI function reads the runtime config
const httpLink = new HttpLink({
  uri: () => _graphqlUrl,
});

// Add Authorization Header
const authLink = new SetContextLink(({ headers }) => {
  const token = getLocalStorageValue(localStorageKey.wovenToken);
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
});

// Flag to prevent multiple concurrent logout operations
let isLoggingOut = false;

// Error handling link - intercepts all GraphQL/network errors
const errorLink = new ErrorLink(({ error }) => {
  const path = globalThis?.location?.pathname;
  let isAuthError = false;

  // Check for network errors with 401 status
  if (ServerError.is(error) && error.statusCode === 401) {
    isAuthError = true;
  }

  // Check for GraphQL errors indicating authentication failure
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, extensions }) => {
      if (message === 'Not authenticated' || extensions?.code === 'UNAUTHENTICATED') {
        isAuthError = true;
      }
    });
  }

  // Handle authentication errors - redirect to login (only once)
  if (isAuthError && !isLoggingOut) {
    isLoggingOut = true;
    if (isRouteProtected(path)) {
      showToast(errorMessages.notAuthenticated, 'error');
    }
    logout();
    // Clear Apollo cache and redirect - fire and forget since page reload resets everything
    client.clearStore().catch(() => { }).finally(() => {
      globalThis.location.replace('/login');
    });
  }
});

// Combine authLink, errorLink, and httpLink - single Apollo client instance for the entire app
const client = new ApolloClient({
  link: ApolloLink.from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
  // Preserve Apollo Client 3 behavior: notifyOnNetworkStatusChange defaults to true in v4,
  // which would cause loading=true on every refetch() call (e.g. after mutations).
  defaultOptions: {
    watchQuery: {
      notifyOnNetworkStatusChange: false,
    },
  },
});

/**
 * Helper function for making GraphQL requests
 * @param {string} query - GraphQL query or mutation string
 * @param {Record<string, any>} [variables] - Variables for the GraphQL operation
 * @returns {Promise<any>} - The response data
 */
export const graphqlRequest = async <TVariables extends OperationVariables = OperationVariables, TResult = unknown>(
  query: TypedDocumentNode<TResult, TVariables> | DocumentNode | string,
  variables?: TVariables,
  fetchPolicy: FetchPolicy = 'network-only',
): Promise<TResult> => {
  try {
    const response = await client.query({
      query: typeof query === 'string' ? gql(query) : query,
      variables,
      fetchPolicy,
    });

    return response.data as TResult;
  } catch (error) {
    throw new Error(handleError(error), { cause: error });
  }
};

export const graphqlMutation = async <TVariables extends OperationVariables = OperationVariables, TResult = unknown>(
  mutation: TypedDocumentNode<TResult, TVariables> | DocumentNode,
  variables?: TVariables,
): Promise<TResult> => {
  try {
    const response = await client.mutate({
      mutation: mutation as DocumentNode,
      variables,
    });

    return response.data as TResult;
  } catch (error) {
    throw new Error(handleError(error), { cause: error });
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleError = (error: any): string => {
  // If logout is already in progress, return empty string to avoid duplicate handling
  if (isLoggingOut) {
    return '';
  }

  const path = globalThis?.location?.pathname;

  // Extract error message for forms and UI display
  let errorMessage: string;

  if (CombinedGraphQLErrors.is(error)) {
    errorMessage = error.errors[0]?.message ?? errorMessages.unknownError;
  } else {
    errorMessage = error?.message ?? (typeof error === 'string' ? error : errorMessages.unknownError);
  }

  // Handle authorization errors (different from authentication)
  if (errorMessage === 'You don\'t have permission to perform this action' && isRouteProtected(path)) {
    showToast(errorMessages.unauthorized, 'error');
    globalThis.location.replace('/projects');
    return '';
  }

  // Return error message for form validation and UI display
  return errorMessage;
};

export default client;
